import { gutter, GutterMarker, EditorView, Decoration } from "@codemirror/view"
import { StateField, StateEffect, RangeSetBuilder } from "@codemirror/state"
import type { DecorationSet } from "@codemirror/view"

export interface ChangedLinesState {
  added: number[]
  removed: number[]
  modified: number[]
}

const emptyChangedLines: ChangedLinesState = {
  added: [],
  removed: [],
  modified: [],
}

class AddedLineMarker extends GutterMarker {
  toDOM() {
    const marker = document.createElement("div")
    marker.className = "cm-gutter-added"
    marker.title = "Line added"
    return marker
  }
}

class RemovedLineMarker extends GutterMarker {
  toDOM() {
    const marker = document.createElement("div")
    marker.className = "cm-gutter-removed"
    marker.innerHTML = "▸"
    marker.title = "Line removed"
    return marker
  }
}

const addedMarker = new AddedLineMarker()
const removedMarker = new RemovedLineMarker()

const addedDecoration = Decoration.line({ class: "cm-line-added" })
const removedDecoration = Decoration.line({ class: "cm-line-removed" })

const clampLineNumber = (lineNumber: number, view: EditorView) => {
  if (lineNumber < 1) return 1
  if (lineNumber > view.state.doc.lines) return view.state.doc.lines
  return lineNumber
}

export const updateChangedLinesEffect = StateEffect.define<ChangedLinesState>()

const changedLinesField = StateField.define<ChangedLinesState>({
  create() {
    return emptyChangedLines
  },
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(updateChangedLinesEffect)) {
        return effect.value
      }
    }
    return value
  },
})

const changeDecorationsField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(updateChangedLinesEffect)) {
        const builder = new RangeSetBuilder<Decoration>()
        const doc = tr.state.doc

        // CodeMirror requires ranges to be added in sorted order and deduplicated
        // Remove duplicates, sort arrays defensively, and filter invalid line numbers
        const validAdded = Array.from(new Set(effect.value.added))
          .filter(lineNum => lineNum >= 1 && lineNum <= doc.lines)
          .sort((a, b) => a - b)
        const validRemoved = Array.from(new Set(effect.value.removed))
          .filter(lineNum => lineNum >= 1 && lineNum <= doc.lines)
          .sort((a, b) => a - b)

        // Combine and sort all line numbers to ensure proper ordering
        // CodeMirror requires ALL ranges (added + removed) to be added in sorted order
        const allLineNumbers = [
          ...validAdded.map(num => ({ num, type: 'added' as const })),
          ...validRemoved.map(num => ({ num, type: 'removed' as const }))
        ].sort((a, b) => a.num - b.num)

        // Add ranges in sorted order (mixed added/removed)
        allLineNumbers.forEach(({ num: lineNumber, type }) => {
          try {
            if (doc.lines === 0) return
            const line = doc.line(lineNumber)
            const decoration = type === 'added' ? addedDecoration : removedDecoration
            builder.add(line.from, line.from, decoration)
          } catch (error) {
            // Silently skip invalid line numbers to avoid console spam
          }
        })

        return builder.finish()
      }
    }

    if (tr.docChanged) {
      return decorations.map(tr.changes)
    }

    return decorations
  },
  provide: (field) => EditorView.decorations.from(field),
})

export function changeGutter() {
  return [
    changedLinesField,
    changeDecorationsField,
    gutter({
      class: "cm-change-gutter",
      lineMarker(view, line) {
        const state = view.state.field(changedLinesField)
        const lineNumber = view.state.doc.lineAt(line.from).number

        if (state.added.includes(lineNumber)) {
          return addedMarker
        }

        if (state.removed.includes(lineNumber)) {
          return removedMarker
        }

        return null
      },
    }),
    EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const { doc } = update.state
        const state = update.state.field(changedLinesField)
        const sanitizedRemoved = state.removed
          .map((line) => clampLineNumber(line, update.view))
          .filter((line) => line >= 1 && line <= doc.lines)

        if (sanitizedRemoved.length !== state.removed.length) {
          update.view.dispatch({
            effects: updateChangedLinesEffect.of({
              ...state,
              removed: sanitizedRemoved,
            }),
          })
        }
      }
    }),
  ]
}

export function getEmptyChangedLines(): ChangedLinesState {
  return { ...emptyChangedLines }
}

